import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, unref as _unref, createBlock as _createBlock, normalizeClass as _normalizeClass, withDirectives as _withDirectives, Fragment as _Fragment } from "vue";
const _hoisted_1 = {
    class: "sr-only",
    role: "status"
};
const _hoisted_2 = {
    key: 0,
    class: "media-player__unavailable"
};
const _hoisted_3 = { class: "media-player__unavailable-content" };
const _hoisted_4 = {
    key: 1,
    class: "media-player__processing"
};
const _hoisted_5 = { class: "media-player__processing-content" };
const _hoisted_6 = { class: "media-player__processing-title" };
const _hoisted_7 = { class: "progress rc-backgroundProcess" };
const _hoisted_8 = ["aria-label", "aria-valuenow"];
const _hoisted_9 = { class: "rc-percent" };
const _hoisted_10 = { class: "media-player__processing-note" };
const _hoisted_11 = {
    key: 2,
    class: "media-player__error"
};
const _hoisted_12 = { class: "media-player__error-content" };
const _hoisted_13 = { class: "media-player__error-title" };
const _hoisted_14 = {
    key: 0,
    class: "media-player__error-message"
};
const _hoisted_15 = {
    key: 1,
    class: "media-player__error-message"
};
const _hoisted_16 = {
    key: 0,
    class: "media-player__overlay"
};
const _hoisted_17 = { class: "media-player__overlay-actions" };
const _hoisted_18 = ["href", "aria-label"];
const _hoisted_19 = ["aria-label"];
const _hoisted_20 = ["disablepictureinpicture", "poster", "aria-label"];
const _hoisted_21 = ["src", "type"];
const _hoisted_22 = ["aria-label"];
const _hoisted_23 = ["aria-label"];
import { ref, shallowRef, computed, inject, onMounted, onBeforeUnmount, watch, nextTick, useTemplateRef } from "vue";
import { useVideoPip } from "@/common/composables/video-pip";
import { vTooltip } from "@/common/directives/tip-directive";
// Components
import SvgIcon from "@/common/components/SvgIcon.vue";
import { MediaPlayerStatus } from "@/types/media-player";
// Setup
export default /*@__PURE__*/ _defineComponent({
    __name: 'MediaPlayer',
    props: /*@__PURE__*/ _mergeModels({
        status: {},
        textTracks: { default: () => [] },
        streamUrl: { default: undefined },
        fileSizeBytes: { default: undefined },
        downloadUrl: { default: undefined },
        mimeType: { default: undefined },
        progress: { default: 0 },
        failureReason: { default: undefined },
        posterUrl: { default: (props) => {
                const isAudio = props.mimeType?.startsWith("audio/");
                return isAudio ? "/_inc/images/audio-placeholder@2x.png" : "";
            } },
        showThumbnailAction: { type: Boolean, default: false },
        enablePip: { type: Boolean, default: false },
        enableNativePip: { type: Boolean, default: false },
        pipClearances: { default: undefined }
    }, {
        "currentTime": { default: 0 },
        "currentTimeModifiers": {},
        "isPlaying": { type: Boolean, ...{ default: false } },
        "isPlayingModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["set-thumbnail", "seeked"], ["update:currentTime", "update:isPlaying"]),
    setup(__props, { expose: __expose, emit: __emit }) {
        /**
         * MediaPlayer — Vue 3 port of video-player.inc
         *
         * States (mirroring the JSP):
         *   1. Unavailable  — no streamUrl / asset missing
         *   2. Processing   — still encoding; shows progress bar + percentage
         *   3. Failed       — encoding/processing failed; shows error message
         *   4. Ready        — VideoJS player (video & audio)
         */
        const props = __props;
        const emit = __emit;
        const i18n = inject("i18n");
        const currentTime = _useModel(__props, "currentTime");
        const isPlaying = _useModel(__props, "isPlaying");
        const thumbnailUpdated = ref(false);
        // shallowRef: called through a reactive proxy, video.js adds a track to remoteTextTracks() but not textTracks(), so the caption menu stays empty.
        const player = shallowRef(null);
        let rafId = null;
        const startTimeLoop = () => {
            if (rafId !== null)
                return;
            const tick = () => {
                if (!player.value) {
                    rafId = null;
                    return;
                }
                currentTime.value = player.value.currentTime();
                if (thumbnailUpdated.value)
                    thumbnailUpdated.value = false;
                rafId = requestAnimationFrame(tick);
            };
            rafId = requestAnimationFrame(tick);
        };
        const stopTimeLoop = () => {
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
                rafId = null;
            }
        };
        // Template refs
        const videoElement = useTemplateRef("videoElement");
        const observerTarget = useTemplateRef("observerTarget");
        const playerContainer = useTemplateRef("playerContainer");
        const isReady = computed(() => props.status !== MediaPlayerStatus.Processing);
        const isFailed = computed(() => !isReady.value && !!props.failureReason);
        const isProcessing = computed(() => !isReady.value && !isFailed.value);
        const isUnavailable = computed(() => isReady.value && !props.streamUrl);
        const isAudioOnly = computed(() => props.mimeType?.startsWith("audio/") ?? false);
        // Playback uses the transcoded file (audio=mp3, video=mp4)
        const playbackMimeType = computed(() => (isAudioOnly.value ? "audio/mpeg" : "video/mp4"));
        const progressPercent = computed(() => Math.min(100, Math.max(0, Math.round(props.progress))));
        // Mini-player (Picture-in-Picture): floats the player when it scrolls out of view while playing.
        const { isPip: isPipActive, showPipExitAnnouncement, dismiss: dismissPip, placeholderStyle, pipStyle, pipHorizontalAnchor, pipPhysicalSide, togglePipAnchor, isDragging: isPipDragging, suppressInsetMotion: isPipInsetMotionSuppressed, onPipPointerDown } = useVideoPip(observerTarget, isPlaying, {
            enabled: () => props.enablePip && !props.enableNativePip,
            contentRef: playerContainer,
            clearances: () => props.pipClearances
        });
        const pipStatusAnnouncement = computed(() => {
            if (isPipActive.value) {
                return isAudioOnly.value
                    ? i18n.t("Audio player moved to floating mini player")
                    : i18n.t("Video player moved to floating mini player");
            }
            if (showPipExitAnnouncement.value) {
                return isAudioOnly.value
                    ? i18n.t("Audio player returned to inline view")
                    : i18n.t("Video player returned to inline view");
            }
            return "";
        });
        const pipMoveText = computed(() => {
            if (isAudioOnly.value) {
                return pipPhysicalSide.value === "left"
                    ? i18n.t("Move audio player to right side")
                    : i18n.t("Move audio player to left side");
            }
            return pipPhysicalSide.value === "left"
                ? i18n.t("Move video player to right side")
                : i18n.t("Move video player to left side");
        });
        const pipHideText = computed(() => (isAudioOnly.value ? i18n.t("Hide audio player") : i18n.t("Hide video player")));
        const playerClasses = computed(() => [
            "media-player__player",
            {
                "media-player__player--audio": isAudioOnly.value,
                "media-player__player--playing": isPlaying.value,
                "media-player__player--pip": isPipActive.value,
                "media-player__player--pip-anchored-start": isPipActive.value && pipHorizontalAnchor.value === "start",
                "media-player__player--pip-dragging": isPipDragging.value,
                "media-player__player--pip-no-motion": isPipInsetMotionSuppressed.value
            }
        ]);
        const onDismissPip = () => {
            dismissPip();
            player.value?.pause();
            // Close button unmounts; refocus the video for keyboard users, without scrolling it into view.
            void nextTick(() => videoElement.value?.focus?.({ preventScroll: true }));
        };
        /**
         * Initialize VideoJS player (mirrors the JSP's initVideoPlayer IIFE).
         */
        const initializePlayer = () => {
            if (!videoElement.value)
                return;
            // Idempotent: both onMounted and the readiness watch can reach here.
            if (player.value)
                return;
            const videojs = window.videojs;
            if (!videojs) {
                console.error("[MediaPlayer] VideoJS not available on window");
                return;
            }
            const p = videojs(videoElement.value, {
                controls: true,
                preload: "auto",
                fluid: !isAudioOnly.value,
                controlBar: {
                    pictureInPictureToggle: props.enableNativePip,
                    // Video.js rewrites this hidden tooltip every frame once a tab regains visibility, restyling the transcript.
                    progressControl: { seekBar: { playProgressBar: { timeTooltip: false } } }
                }
            });
            p.on("play", () => {
                isPlaying.value = true;
                startTimeLoop();
            });
            p.on("pause", () => {
                isPlaying.value = false;
                stopTimeLoop();
            });
            p.on("ended", () => {
                isPlaying.value = false;
                stopTimeLoop();
            });
            p.on("seeked", () => emit("seeked"));
            player.value = p;
            syncTextTracks();
        };
        /**
         * Attach caption tracks via video.js's remote-track API (mirrors legacy video-js.js setTextTracks):
         * clear prior remote tracks, then add the current set. Empty list ⇒ none (External Content passes none).
         */
        const syncTextTracks = () => {
            const p = player.value;
            if (!p)
                return;
            // Attached before metadata loads, a track reaches remoteTextTracks() but never textTracks(), so the caption menu ignores it.
            if ((p.readyState() ?? 0) < 1) {
                // off first: the track list can change several times before metadata, and each pending handler would re-run the whole sync.
                p.off("loadedmetadata", syncTextTracks);
                p.one("loadedmetadata", syncTextTracks);
                return;
            }
            const existing = p.remoteTextTracks();
            if (existing) {
                for (let i = existing.length - 1; i >= 0; i--) {
                    p.removeRemoteTextTrack(existing[i]);
                }
            }
            for (const track of props.textTracks) {
                p.addRemoteTextTrack(track, true);
            }
        };
        const onSetThumbnail = () => {
            if (!player.value)
                return;
            const timestampMs = Math.round(player.value.currentTime() * 1000);
            emit("set-thumbnail", timestampMs);
            thumbnailUpdated.value = true;
        };
        const disposePlayer = () => {
            if (player.value) {
                player.value.dispose();
                player.value = null;
            }
        };
        onMounted(() => {
            if (isReady.value && !isUnavailable.value) {
                initializePlayer();
            }
        });
        // Init once ready AND a stream is available — the host may push status:"ready"
        // before streamUrl (IDI transcript) or flip ready after the stream is known (External Content).
        watch(() => isReady.value && !isUnavailable.value, (canPlay) => {
            if (canPlay)
                void nextTick(initializePlayer);
        });
        onBeforeUnmount(() => {
            stopTimeLoop();
            disposePlayer();
            // dispose() emits no pause/ended event, so clear the playback model
            // ourselves to avoid leaving consumers with stale "playing" state.
            isPlaying.value = false;
        });
        // Update poster when thumbnailUrl changes (e.g. after setThumbnail)
        watch(() => props.posterUrl, (next) => {
            if (player.value && next) {
                player.value.poster(next);
            }
        });
        // Deep: the IDI host passes a cached computed, so contents change without a new array identity.
        watch(() => props.textTracks, () => syncTextTracks(), { deep: true });
        // Seek when parent changes currentTime externally
        watch(currentTime, (newTime) => {
            if (!player.value)
                return;
            const playerTime = player.value.currentTime();
            if (Math.abs(newTime - playerTime) > 0.5) {
                player.value.currentTime(newTime);
                if (player.value.paused()) {
                    player.value.play();
                }
            }
        });
        __expose({ player: videoElement, pause: () => player.value?.pause() });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", {
                ref_key: "observerTarget",
                ref: observerTarget,
                class: "media-player"
            }, [
                _createElementVNode("div", _hoisted_1, _toDisplayString(pipStatusAnnouncement.value), 1),
                (isUnavailable.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                        _createElementVNode("div", _hoisted_3, [
                            _createVNode(SvgIcon, {
                                icon: "icon-video",
                                class: "media-player__icon--lg"
                            }),
                            _createElementVNode("p", null, _toDisplayString(isAudioOnly.value ? _ctx.$t("Audio not available") : _ctx.$t("Video not available")), 1)
                        ])
                    ]))
                    : (isProcessing.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                            _createElementVNode("div", _hoisted_5, [
                                _createElementVNode("div", _hoisted_6, _toDisplayString(isAudioOnly.value ? _ctx.$t("Preparing audio for Playback") : _ctx.$t("Preparing video for Playback")), 1),
                                _createElementVNode("div", _hoisted_7, [
                                    _createElementVNode("div", {
                                        class: "progress-bar",
                                        role: "progressbar",
                                        "aria-label": _ctx.$t('Processing progress'),
                                        "aria-valuenow": progressPercent.value,
                                        "aria-valuemin": "0",
                                        "aria-valuemax": "100",
                                        style: _normalizeStyle({ width: `${progressPercent.value}%` })
                                    }, [...(_cache[2] || (_cache[2] = [
                                            _createElementVNode("div", { class: "rc-animCheck" }, [
                                                _createElementVNode("div")
                                            ], -1)
                                        ]))], 12, _hoisted_8),
                                    _createElementVNode("div", _hoisted_9, _toDisplayString(progressPercent.value) + "%", 1)
                                ]),
                                _createElementVNode("div", _hoisted_10, _toDisplayString(isAudioOnly.value
                                    ? _ctx.$t("You can leave this page while the audio is being processed")
                                    : _ctx.$t("You can leave this page while the video is being processed")), 1)
                            ])
                        ]))
                        : (isFailed.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                                _createElementVNode("div", _hoisted_12, [
                                    _createVNode(SvgIcon, {
                                        icon: "icon-warning",
                                        class: "media-player__icon--lg media-player__icon--error"
                                    }),
                                    _createElementVNode("p", _hoisted_13, _toDisplayString(isAudioOnly.value ? _ctx.$t("Audio Processing Error") : _ctx.$t("Video Processing Error")), 1),
                                    (__props.failureReason)
                                        ? (_openBlock(), _createElementBlock("p", _hoisted_14, _toDisplayString(__props.failureReason), 1))
                                        : (_openBlock(), _createElementBlock("p", _hoisted_15, _toDisplayString(isAudioOnly.value
                                            ? _ctx.$t("There was a problem processing this audio.")
                                            : _ctx.$t("There was a problem processing this video.")) + " " + _toDisplayString(_ctx.$t("Please try uploading again.")), 1))
                                ])
                            ]))
                            : (isReady.value)
                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                                    (_unref(isPipActive))
                                        ? (_openBlock(), _createElementBlock("div", {
                                            key: 0,
                                            class: "media-player__pip-placeholder",
                                            style: _normalizeStyle(_unref(placeholderStyle))
                                        }, [
                                            _createVNode(SvgIcon, {
                                                icon: isAudioOnly.value ? 'icon-audio-file' : 'icon-video',
                                                class: "media-player__pip-placeholder-icon"
                                            }, null, 8, ["icon"])
                                        ], 4))
                                        : _createCommentVNode("", true),
                                    _createElementVNode("div", {
                                        ref_key: "playerContainer",
                                        ref: playerContainer,
                                        class: _normalizeClass(playerClasses.value),
                                        style: _normalizeStyle(_unref(pipStyle)),
                                        onPointerdown: _cache[1] || (_cache[1] =
                                            //@ts-ignore
                                            (...args) => (_unref(onPipPointerDown) && _unref(onPipPointerDown)(...args)))
                                    }, [
                                        (__props.downloadUrl || __props.showThumbnailAction)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                                                _createElementVNode("div", _hoisted_17, [
                                                    (__props.downloadUrl && !_unref(isPipActive))
                                                        ? (_openBlock(), _createElementBlock("a", {
                                                            key: 0,
                                                            class: "media-player__overlay-btn",
                                                            href: __props.downloadUrl,
                                                            target: "_blank",
                                                            "aria-label": _ctx.$t('Download')
                                                        }, [
                                                            _createVNode(SvgIcon, { icon: "icon-download" })
                                                        ], 8, _hoisted_18))
                                                        : _createCommentVNode("", true),
                                                    (__props.showThumbnailAction && !isAudioOnly.value)
                                                        ? (_openBlock(), _createElementBlock("button", {
                                                            key: 1,
                                                            type: "button",
                                                            class: _normalizeClass(['media-player__overlay-btn', { 'media-player__overlay-btn--updated': thumbnailUpdated.value }]),
                                                            "aria-label": _ctx.$t('Set as thumbnail'),
                                                            onClick: onSetThumbnail
                                                        }, [
                                                            (thumbnailUpdated.value)
                                                                ? (_openBlock(), _createBlock(SvgIcon, {
                                                                    key: 0,
                                                                    icon: "icon-check-skinny"
                                                                }))
                                                                : (_openBlock(), _createBlock(SvgIcon, {
                                                                    key: 1,
                                                                    icon: "icon-photo-3"
                                                                }))
                                                        ], 10, _hoisted_19))
                                                        : _createCommentVNode("", true)
                                                ])
                                            ]))
                                            : _createCommentVNode("", true),
                                        _createElementVNode("video", {
                                            ref_key: "videoElement",
                                            ref: videoElement,
                                            class: "video-js",
                                            controls: "",
                                            preload: "none",
                                            disablepictureinpicture: __props.enableNativePip ? undefined : true,
                                            poster: __props.posterUrl || undefined,
                                            "aria-label": isAudioOnly.value ? _ctx.$t('Audio player') : _ctx.$t('Video player'),
                                            "webkit-playsinline": "",
                                            playsinline: ""
                                        }, [
                                            (__props.streamUrl)
                                                ? (_openBlock(), _createElementBlock("source", {
                                                    key: 0,
                                                    src: __props.streamUrl,
                                                    type: playbackMimeType.value
                                                }, null, 8, _hoisted_21))
                                                : _createCommentVNode("", true)
                                        ], 8, _hoisted_20),
                                        (_unref(isPipActive))
                                            ? _withDirectives((_openBlock(), _createElementBlock("button", {
                                                key: 1,
                                                type: "button",
                                                class: "media-player__pip-move",
                                                "aria-label": pipMoveText.value,
                                                onClick: _cache[0] || (_cache[0] =
                                                    //@ts-ignore
                                                    (...args) => (_unref(togglePipAnchor) && _unref(togglePipAnchor)(...args)))
                                            }, [
                                                _createVNode(SvgIcon, { icon: "icon-sort-arrows-4" })
                                            ], 8, _hoisted_22)), [
                                                [
                                                    _unref(vTooltip),
                                                    pipMoveText.value,
                                                    void 0,
                                                    {
                                                        nowrap: true,
                                                        nw: true
                                                    }
                                                ]
                                            ])
                                            : _createCommentVNode("", true),
                                        (_unref(isPipActive))
                                            ? _withDirectives((_openBlock(), _createElementBlock("button", {
                                                key: 2,
                                                type: "button",
                                                class: "media-player__pip-close",
                                                "aria-label": pipHideText.value,
                                                onClick: onDismissPip
                                            }, [
                                                _createVNode(SvgIcon, { icon: "icon-close-3" })
                                            ], 8, _hoisted_23)), [
                                                [
                                                    _unref(vTooltip),
                                                    pipHideText.value,
                                                    void 0,
                                                    {
                                                        nowrap: true,
                                                        nw: true
                                                    }
                                                ]
                                            ])
                                            : _createCommentVNode("", true)
                                    ], 38)
                                ], 64))
                                : _createCommentVNode("", true)
            ], 512));
        };
    }
});
